import type { ComponentType } from 'react'
import {
  Package,
  Lightning,
  Wrench,
  ShieldCheck,
  Lightbulb,
  Plug,
  Circuitry,
  Warning,
  Power,
  BatteryCharging,
  Cpu,
  Engine,
  GearSix,
  Robot,
  Toolbox,
  HardDrives,
  Fan,
  ThermometerHot,
  Database,
  Timer,
  Gauge,
  VideoCamera,
  Bell,
  WifiHigh,
  Barricade,
  Headlights,
  CarProfile,
  Car,
  FrameCorners,
} from '@phosphor-icons/react'

export type CategoryVisual =
  | { type: 'icon'; icon: ComponentType<{ className?: string }> }
  | { type: 'image'; src: string; alt: string }

const CATEGORY_ICON_URLS = {
  acionamentoControle: '/category-icons/acionamento-controle.png',
  comandoSinalizacao: '/category-icons/comando-sinalizacao.png',
  protecao: '/category-icons/protecao.png',
  chaveTransferencia: '/category-icons/chave-transferencia.png',
  automacaoSeguranca: '/category-icons/automacao-seguranca.png',
  medidoresControladores: '/category-icons/medidores-controladores.png',
  solar: '/category-icons/solar.png',
  iluminacao: '/category-icons/iluminacao.png',
  sensoresFimDeCurso: '/category-icons/sensores-fim-de-curso.png',
  outrasCategorias: '/category-icons/outras-categorias.png',
} as const

export const normalizeCategoryToken = (value?: string | null) =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()

export const formatCategoryLabel = (label: string) => {
  if (normalizeCategoryToken(label) === 'categorias') {
    return 'Outras Categorias'
  }
  return label.replace(/\bE\b/g, 'e')
}

const resolveAutoPartsCategoryIcon = (token: string) => {
  if (token.includes('faro')) return Headlights
  if (token.includes('lampada') || token.includes('lanterna')) return Lightbulb
  if (token.includes('mecanica')) return Wrench
  if (token.includes('motor') || token.includes('agregado')) return Engine
  if (token.includes('parachoque') || token.includes('lataria')) return Car
  if (token.includes('paralama') || token.includes('parabarro')) return ShieldCheck
  if (token.includes('retrovisor')) return CarProfile
  if (token.includes('vidro')) return FrameCorners
  if (token.includes('acessorio')) return Package
  return null
}

export const getCategoryVisual = (category: Pick<{ descricao: string; slug?: string }, 'descricao' | 'slug'>): CategoryVisual => {
  const name = normalizeCategoryToken(category.descricao)
  const slug = normalizeCategoryToken(category.slug)
  const token = `${slug} ${name}`.trim()

  const autoPartsIcon = resolveAutoPartsCategoryIcon(token)
  if (autoPartsIcon) {
    return { type: 'icon', icon: autoPartsIcon }
  }

  if (token.includes('acionamento') || token.includes('controle')) {
    return {
      type: 'image',
      src: CATEGORY_ICON_URLS.acionamentoControle,
      alt: category.descricao,
    }
  }
  if (token.includes('comando') || token.includes('sinaliza')) {
    return {
      type: 'image',
      src: CATEGORY_ICON_URLS.comandoSinalizacao,
      alt: category.descricao,
    }
  }
  if (token.includes('protecao')) {
    return {
      type: 'image',
      src: CATEGORY_ICON_URLS.protecao,
      alt: category.descricao,
    }
  }
  if (token.includes('automacao') && token.includes('seguranca')) {
    return {
      type: 'image',
      src: CATEGORY_ICON_URLS.automacaoSeguranca,
      alt: category.descricao,
    }
  }
  if ((token.includes('medidor') || token.includes('medicao')) && token.includes('control')) {
    return {
      type: 'image',
      src: CATEGORY_ICON_URLS.medidoresControladores,
      alt: category.descricao,
    }
  }
  if (token.includes('solar') || token.includes('fotovolta')) {
    return {
      type: 'image',
      src: CATEGORY_ICON_URLS.solar,
      alt: category.descricao,
    }
  }
  if (token.includes('iluminacao')) {
    return {
      type: 'image',
      src: CATEGORY_ICON_URLS.iluminacao,
      alt: category.descricao,
    }
  }
  if (token.includes('sensor') || token.includes('fim de curso')) {
    return {
      type: 'image',
      src: CATEGORY_ICON_URLS.sensoresFimDeCurso,
      alt: category.descricao,
    }
  }
  if (token.includes('chave') || token.includes('transfer')) {
    return {
      type: 'image',
      src: CATEGORY_ICON_URLS.chaveTransferencia,
      alt: category.descricao,
    }
  }
  if (token.includes('seguranca')) {
    return { type: 'icon', icon: ShieldCheck }
  }
  if (token.includes('automacao')) return { type: 'icon', icon: Cpu }
  if (token.includes('iluminacao') || token.includes('luminaria') || token.includes('luz')) {
    return { type: 'icon', icon: Lightbulb }
  }
  if (token.includes('categoria')) {
    return {
      type: 'image',
      src: CATEGORY_ICON_URLS.outrasCategorias,
      alt: category.descricao,
    }
  }
  if (token.includes('eletrica') || token.includes('eletrico') || token.includes('energia')) {
    return { type: 'icon', icon: Lightning }
  }
  if (token.includes('motor')) {
    return { type: 'icon', icon: Engine }
  }
  if (token.includes('tomada') || token.includes('plug') || token.includes('conector')) {
    return { type: 'icon', icon: Plug }
  }
  if (token.includes('ferramenta')) {
    return { type: 'icon', icon: Wrench }
  }
  if (token.includes('disjuntor') || token.includes('circuito')) {
    return { type: 'icon', icon: Circuitry }
  }
  if (token.includes('alarme')) {
    return { type: 'icon', icon: Bell }
  }
  if (token.includes('bateria') || token.includes('fonte') || token.includes('alimentacao')) {
    return { type: 'icon', icon: BatteryCharging }
  }
  if (token.includes('ventilacao') || token.includes('ventilador') || token.includes('exaustor')) {
    return { type: 'icon', icon: Fan }
  }
  if (token.includes('temperatura') || token.includes('termostato') || token.includes('aquecedor')) {
    return { type: 'icon', icon: ThermometerHot }
  }
  if (token.includes('seguranca') || token.includes('epi') || token.includes('sinaliza')) {
    return { type: 'icon', icon: Warning }
  }
  if (token.includes('automacao') || token.includes('robotica')) {
    return { type: 'icon', icon: Robot }
  }
  if (token.includes('caixa') || token.includes('quadro') || token.includes('painel')) {
    return { type: 'icon', icon: Toolbox }
  }
  if (token.includes('disco') || token.includes('armazenamento') || token.includes('storage')) {
    return { type: 'icon', icon: HardDrives }
  }
  if (token.includes('medicao') || token.includes('medidor') || token.includes('sensor')) {
    return { type: 'icon', icon: Gauge }
  }
  if (token.includes('tempo') || token.includes('temporizador') || token.includes('timer')) {
    return { type: 'icon', icon: Timer }
  }
  if (token.includes('camera') || token.includes('cftv') || token.includes('vigilancia')) {
    return { type: 'icon', icon: VideoCamera }
  }
  if (token.includes('rede') || token.includes('network') || token.includes('wifi') || token.includes('comunicacao')) {
    return { type: 'icon', icon: WifiHigh }
  }
  if (token.includes('barreira') || token.includes('cerca') || token.includes('isolamento')) {
    return { type: 'icon', icon: Barricade }
  }
  if (token.includes('engrenagem') || token.includes('mecanica') || token.includes('transmissao')) {
    return { type: 'icon', icon: GearSix }
  }
  if (token.includes('potencia') || token.includes('power')) {
    return { type: 'icon', icon: Power }
  }
  if (token.includes('data') || token.includes('dados') || token.includes('informacao')) {
    return { type: 'icon', icon: Database }
  }

  return { type: 'icon', icon: Package }
}
